import React from 'react';

import Accordion from 'components/Accordion';
import DatePicker from 'components/DatePicker';
import RadioButton from 'components/DesignSystem/RadioButton';
import CalendarioLegenda from 'components/Relatorios/shared/Aprovacao/CalendarioLegenda';
import useDessemControlPanel from 'hooks/Relatorios/Dessem/useDessemControlPanel';

import './index.scss';

/**
 * Painel de controle do Informe DESSEM: `Accordion` do design system + `DatePicker` inline
 * no `RightSidebar`, com o filtro **Cenário**. Os cenários vêm do `GET options` do dia
 * (`useDessemCenarios`: label, ordem e disponibilidade do backend; sem o campo, cai nos
 * Early/Preliminar/POS_ONS do config). Cenário indisponível no dia fica desabilitado (todos,
 * enquanto a lista do dia carrega) e, se for o aplicado, o painel passa para o padrão do dia
 * (ou o primeiro disponível). Diferente do
 * DECOMP, os blocos "Data do Informe" e "Cenário" ficam abertos AO MESMO TEMPO
 * (`defaultActiveKey` em array + `alwaysOpen`, como no Figma): abrir o calendário não
 * recolhe os radios — cada bloco ainda pode ser recolhido sozinho, sem fechar o outro.
 *
 * Sem botão "Atualizar Data/Cenário" (Planejamento, 13/10/2026): clicar num dia ou trocar o
 * cenário já aplica no slice e a seção recarrega. O custo é baixo — o cliente só lê o snapshot
 * publicado e só consegue clicar em dia publicado, e o RTK Query guarda cada (data, cenário) em
 * cache. Para o admin, cada combinação aplicada que tem estudo no Core vira rascunho (DRAFT lazy
 * do backend), inclusive a intermediária (dia novo com o cenário anterior, antes de trocar o
 * cenário) e um clique errado: aceito pelo Planejamento junto com a retirada do botão.
 *
 * O dia do calendário é a **Data do Informe** (D): o informe de D mostra o deck DESSEM montado
 * para D+1 com base no deck oficial CCEE de D. A API já fala em D (`data_rodada`).
 *
 * Com a aprovação habilitada (`config.habilitado()`, PLANO v2, T14), o calendário ganha
 * bolinhas de status para o admin (`renderDayContents`) e, para o cliente,
 * só permite selecionar dias publicados (`filterDate`) — os demais aparecem
 * acinzentados (`dayClassName`).
 *
 * O teto do calendário (`maxDate`) é o último dia com estudo, `options.max_data_rodada`
 * via `useDessemMaxData` (admin = maior dia com estudo da Ampere no Core; cliente = último
 * dia publicado), reconsultado a cada 5 min: o dia de hoje só libera quando o Early de amanhã
 * chega ao Core. Com um backend sem o campo, o teto cai em `default_data_rodada` e, na falta,
 * em hoje.
 */
export default function DessemControlPanel() {
  const {
    selecionada,
    handleDataChange,
    maxData,
    datePickerProps,
    mostraLegenda,
    cenarios,
    carregando,
    cenarioAplicado,
    handleCenarioChange,
  } = useDessemControlPanel();

  return (
    <div className="dessem-control-panel">
      {/* Os dois blocos abertos (Figma): o cenário fica visível enquanto se escolhe a data. */}
      <Accordion defaultActiveKey={['0', '1']} alwaysOpen>
        <Accordion.Item eventKey="0">
          <Accordion.Header>Data do Informe</Accordion.Header>
          <Accordion.Body className="d-grid">
            <DatePicker
              inline
              selected={selecionada}
              onChange={handleDataChange}
              // Último dia com estudo (ver doc do componente), não amanhã fixo.
              maxDate={maxData}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...datePickerProps}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>Cenário</Accordion.Header>
          <Accordion.Body className="d-grid gap-1">
            {cenarios.map(({ value, label, disponivel }) => (
              <RadioButton
                key={value}
                name="dessem-cenario"
                label={label}
                value={value}
                checked={cenarioAplicado === value}
                disabled={carregando || !disponivel}
                onChange={() => handleCenarioChange(value)}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {mostraLegenda && <CalendarioLegenda isAdmin />}
    </div>
  );
}
